<template>
  <div class="cs-editor" v-if="editstate.cs && ss">
    <div class="cs-editor-head" v-if="!noMenu">
      <div class="cs-editor-menu">
        <button
          v-if="editstate.undo"
          @click="undo()"
          class="jps-text-button"
          :disabled="!editstate.canUndo"
        >
          <BaseIcon name="mdi/undo" />
          {{ lss('元に戻す', 'Undo') }}
        </button>
        <button
          v-if="editstate.redo"
          @click="redo()"
          class="jps-text-button"
          :disabled="!editstate.canRedo"
        >
          <BaseIcon name="mdi/redo" />
          {{ lss('やり直し', 'Redo') }}
        </button>
        <button v-if="!isMys" @click="preview()" class="jps-text-button">
          <BaseIcon name="mdi/eye" />
          {{ lss('プレビュー', 'Preview') }}
        </button>
        <button v-if="isMys" class="jps-text-button" @click="emits('finish')">
          <BaseIcon name="mdi/check" />
          {{ lss('完了', 'Finish') }}
        </button>
        <button
          v-if="editstate.save && !isMys"
          class="jps-text-button"
          @click="editstate.save()"
          :disabled="!editstate.canSave || editstate.isSaving"
        >
          <BaseIcon
            :is-spin="editstate.isSaving"
            :name="editstate.isSaving ? 'mdi/sync' : 'mdi/content-save'"
          />
          {{ lss('保存', 'Save') }}
        </button>
        <BaseDropdown position="bottom-left">
          <template #trigger="">
            <button class="jps-icon-button">
              <BaseIcon name="mdi/dots-horizontal"/>
            </button>
          </template>
          <BaseDropdownItem @click="setImage()" v-if="!isMys">代表画像の設定</BaseDropdownItem>
          <BaseDropdownItem @click="importCs()">{{lss('インポート', 'Import') }}</BaseDropdownItem>
          <BaseDropdownItem @click="exportCs()">{{ lss('エクスポート', 'Export') }}</BaseDropdownItem>
          <BaseDropdownItem :link="true" v-if="csTo">
            <BaseLink opentype="tab" :to="csTo">公開URL</BaseLink>
          </BaseDropdownItem>
          <BaseDropdownItem v-if="showPermissionMenu" @click="manageType()">種別変更</BaseDropdownItem>
          <BaseDropdownItem v-if="showPermissionMenu" @click="managePublish()">公開管理</BaseDropdownItem>
          <BaseDropdownItem v-if="showPermissionMenu" @click="managePermission()">権限管理</BaseDropdownItem>
        </BaseDropdown>
      </div>
    </div>

    <div class="cs-editor-main">
      <div class="cs-editor-search-box">
        <slot name="message" />
        <header class="editor-cs-header cs-part-editor">
          <h2 class="jps-heading-small cs-editor-heading">
            <BaseIcon v-if="isJps" name="koicon/koicon-35" class="jps-red" />
           {{ lss('マイサーチのタイトル', 'Customized Search Title') }}
          </h2>
          <div class="editor-title">
            <label>{{ lss('タイトル', 'Title') }}</label>
            <LangStringEditor
              class="title-field"
              input-class="jps-ul-input"
              :no-lang="isMys"
              :value="editstate.cs.name"
              :rule="rule.fields.name"
              :no-placeholder="true"
            />
          </div>
          <p class="editor-description">
            <label>{{ lss('説明', 'Description') }}</label>
            <LangStringEditor
              class="title-field"
              input-class="jps-ul-input"
              :no-lang="isMys"
              :value="editstate.cs.description"
              :rule="rule.fields.description"
              :no-placeholder="true"
            />
            <UiButtonIcon
              v-if="!isMys"
              tag="button"
              class="jps-icon-button"
              :class="{ 'jps-blue': showHelp }"
              icon-size="size-5"
              icon="mdi/help-circle-outline"
              @click="showHelp = !showHelp"
            />
          </p>
          <p class="cs-editor-help" v-show="showHelp">
            <LangDeltaEditor
              :placeholder="{ ja: '検索ヘルプ', en: 'Search Help' }"
              :lang-delta="editstate.cs.help"
            />
          </p>
          <div class="sample-query" v-if="!isMys">
            <dl>
              <dt
                class="jps-flex-center"
                v-if="editstate.cs.sampleQuery.length > 0"
              >
                {{ ls({ ja: '例', en: 'Example ' }) }}
              </dt>
              <dd
                class="sample-keyword"
                v-for="query in editstate.cs.sampleQuery"
                :key="query.name.ja"
              >
                <UiButtonIcon
                  tag="button"
                  type="button"
                  icon-size="size-6"
                  class="jps-icon-button jps-orange is-size-6"
                  icon="mdi/pencil"
                  @click="editSample(query)"
                />
                <a @click="searchSample(query)">{{ ls(query.name) }}</a>
                <UiButtonIcon
                  tag="button"
                  type="button"
                  icon-size="size-6"
                  class="jps-icon-button jps-orange is-size-6"
                  icon="mdi/close-circle"
                  @click="arrayRemove(editstate.cs.sampleQuery, query)"
                />
              </dd>
              <dd>
                <button
                  type="button"
                  class="jps-round-button jps-orange"
                  @click="editSample()"
                >
                  <BaseIcon
                    class="intext"
                    name="mdi/pencil-plus"
                    size="size-6"
                  />
                  {{ lss('検索例の追加', 'Add search samples') }}
                </button>
                <button
                  v-if="editstate.cs.sampleQuery.length >= 2"
                  type="button"
                  class="jps-round-button is-size-7 jps-orange"
                  @click="sortSample()"
                >
                  <BaseIcon name="mdi/sort" size="size-6" />
                  {{ lss('並べ替え', 'Sort') }}
                </button>
              </dd>
            </dl>
          </div>
        </header>
        <div class="cs-part-editor">
          <h2 class="jps-heading-small cs-editor-heading">
            <BaseIcon v-if="isJps" name="koicon/koicon-12" class="jps-red" />
            {{ lss('検索設定', 'Search Settings') }}
          </h2>
          <SearchBox
            :ss="ss"
            :editable="true"
            @edit-constraint="(e) => editSearchConstraint(e)"
            @delete-constraint="(e) => arrayRemove(editstate.cs.constraints, e)"
          >
            <template #search-menu-addon="">
              <div class="cs-editor-section">
                <label for="cs-keyword-config">
                  {{ lss('キーワード検索の設定', 'Keyword search settings') }}
                </label>
                <button
                  type="button"
                  name="cs-keyword-config"
                  class="jps-round-button jps-orange keyword-config"
                  @click="setKeyword()"
                >
                  <BaseIcon class="intext" name="mdi/pencil" size="size-6" />
                  {{ lss('設定', 'Set') }}
                </button>
              </div>
            </template>
            <template #database-addon="">
              <div class="cs-editor-section">
                <label for="cs-db-config">{{
                  lss('対象データベース', 'Target databases')
                }}</label>
                <button
                  type="button"
                  name="cs-db-config"
                  v-if="!isFixDb"
                  @click="selectDatabase()"
                  class="jps-round-button jps-orange limit-db-button"
                >
                  <BaseIcon class="intext" name="mdi/pencil" size="size-6" />
                  {{ lss('指定', 'Set') }}
                </button>
              </div>
            </template>
            <template #constraint-addon="">
              <div class="cs-editor-section">
                <label for="cs-constraint-config">{{
                  lss('検索プリセット', 'Search constraints')
                }}</label>
                <button
                  type="button"
                  name="cs-constraint-config"
                  class="jps-round-button jps-orange constraint-button"
                  @click="editSearchConstraint()"
                >
                  <BaseIcon
                    class="intext"
                    name="mdi/pencil-plus"
                    size="size-6"
                  />
                  {{ lss('追加', 'Add') }}
                </button>
              </div>
            </template>
            <template #search-detail-menu-addon="">
              <div class="cs-editor-section">
                <label for="cs-facet-config">
                  {{ lss('検索項目UIの追加', 'Add search field') }}
                </label>
                <button
                  type="button"
                  name="cs-facet-config"
                  class="jps-round-button jps-orange"
                  @click="editSearchField()"
                >
                  <BaseIcon
                    class="intext"
                    name="mdi/pencil-plus"
                    size="size-6"
                  />
                  {{ lss('追加', 'Add') }}
                </button>
                <button
                  v-if="Array.isArray(editstate.cs.searchFields) && editstate.cs.searchFields.length >= 2"
                  type="button"
                  class="jps-round-button jps-orange"
                  @click="sortSearchField()"
                >
                  <BaseIcon class="intext" name="mdi/sort" size="size-6" />
                  {{ lss('検索項目並べ替え', 'Sort fields') }}
                </button>
              </div>
            </template>
            <template #search-field-addon="{ field }">
              <div class="search-field-edit">
                <UiButtonIcon
                  tag="button"
                  type="button"
                  class="jps-icon-button jps-orange"
                  icon-size="size-5"
                  icon="mdi/pencil"
                  @click="editSearchField(field)"
                />
                {{ ls(field.name) }}
                <UiButtonIcon
                  tag="button"
                  type="button"
                  class="jps-icon-button jps-orange"
                  icon-size="size-5"
                  icon="mdi/close-circle"
                  @click="arrayRemove(editstate.cs.searchFields, field)"
                />
             </div>
            </template>
          </SearchBox>
        </div>
      </div>
      <div class="cs-part-editor" v-if="ss.searched">
        <UiItemSearchResult v-if="ss.searched" :ss="ss" :editstate="editstate" />
      </div>
    </div>
    <ImageSelectModal ref="imageSelectModal" :image="editstate.cs.image" :ruleField="rule.fields.image" />
    <CsPreviewModal ref="csPreviewModal" :editstate="editstate" />
    <CsImportModal ref="csImportModal" :editstate="editstate" />
    <UiSelectionModal ref="selectionModal" />
    <PublicationModal ref="publicationModal" />
    <PermissionModal ref="permissionModal" />
    <CsKeywordModal ref="csKeywordModal" />
    <CsSampleModal ref="csSampleModal" />
    <UiSortModal ref="sortSampleModal" :renderer="(f) => ls(f.name)" />
    <UiSortModal ref="sortSearchFieldModal" :renderer="(f) => ls(f.name)" />
    <DbSelectorModal ref="dbSelectorModal" />
    <CsFieldModal ref="csFieldModal" />
    <CsConstraintModal ref="csConstraintModal" />
  </div>
</template>

<script lang="ts" setup>
import type {
  ImageSelectModal,
  CsPreviewModal,
  CsImportModal,
  UiSelectionModal,
  PublicationModal,
  PermissionModal,
  CsKeywordModal,
  CsSampleModal,
  UiSortModal,
  DbSelectorModal,
  CsFieldModal,
  CsConstraintModal
} from '#components'

const { ls, lss } = useI18n()
const { initCsForEdit } = useCsEditor()
const { testCustomSearch } = useItem()
const { snackbarMsg } = useSnackbar()
const { hasAdminFor } = useUserStore()
const { isJps } = useServiceType()

interface Props {
  editstate: CsEditState
  noMenu?: boolean
}
const props = withDefaults(defineProps<Props>(),{
  noMenu:false
})
const emits = defineEmits(['finish'])

const isFixDb = computed(() => {
  return props.editstate.db != null
})

const isMys = computed(() => {
  return props.editstate.mode === 'mysearch'
})

const csTo = computed(() => {
  switch (props.editstate.cs.type) {
    case 'NORMAL':
      return `/csearch/${props.editstate.cs.id}`
    case 'NOTE':
      return null
    case 'DB':
      return `/database/${props.editstate.db?.id}`
  }
  return null
})

const rule: ValidationRule = CUSTOM_SEARCH_DEFINITION_RULE
const ss: Ref<SearchStore<Item>> = ref(null)
const showHelp: Ref<boolean> = ref(false)

const setupCs = () => {
  ss.value = useSearch<Item>(
    props.editstate.cs,
    testCustomSearch(props.editstate.cs),
    {
      configSyncRoute: false,
      configHistory: false,
      configNoCache: true,
    }
  )
}

watch(
  () => props.editstate.cs,
  (cs) => {
    if (props.editstate.cs != null && ss.value == null) {
      setupCs()
    }
  },
  { immediate: true }
)

const arrayRemove = (array, value) => {
  removeFromArray(array, value)
  props.editstate.snapshot()
  setupCs()
}

//管理メニュー
const showPermissionMenu = computed(() => {
  return (
    props.editstate.mode === 'management' &&
    hasAdminFor(props.editstate.cs)
  )
})

const undo = async () => {
  props.editstate.undo()
  await nextTick(() => {
    setupCs()
  })
}

const redo = async () => {
  props.editstate.redo()
  await nextTick(() => {
    setupCs()
  })
}

const imageSelectModal = ref<InstanceType<typeof ImageSelectModal>>()
const setImage = async () => {
  try {
    props.editstate.cs.image = await imageSelectModal.value.openAwait()
  } catch (cancel) {}
}

const csPreviewModal = ref<InstanceType<typeof CsPreviewModal>>()
const preview = async () => {
  try {
    await csPreviewModal.value.openAwait()
  } catch (close) {}
}

const exportCs = () => {
  let cs = clone(props.editstate.cs)
  delete cs.edit
  delete cs.permission
  exportJSON(cs, ls(cs.name) + '.json')
}

const csImportModal = ref<InstanceType<typeof CsImportModal>>()
const importCs = async () => {
  try {
    let cs = await csImportModal.value.openAwait()
    if (!cs) return
    //copy fields
    initCsForEdit(cs)
    const target = [
      'name',
      'description',
      'help',
      'databaseIds',
      'searchFields',
      'constraints',
      'sampleQuery',
      'columns',
      'keywordFields',
      'keywordPlaceholder',
      'sorts',
      'image'
    ]
    target.forEach((field) => {
      props.editstate.cs[field] = cs[field]
    })
    props.editstate.cs.sampleQuery.forEach(
      (q) => (q.csid = props.editstate.cs.id)
    )
  } catch (cancel) {}
}

const selectionModal = ref<InstanceType<typeof UiSelectionModal>>()
const manageType = async () => {
  try {
    let type = await selectionModal.value.openAwait({
      options: [
        {
          label: { ja: 'テーマ別検索', en: 'Designed Search' },
          disabled: props.editstate.cs.type === 'NORMAL',
            description: {
              ja: '一般公開の場合、テーマ別検索として、テーマ別検索一覧に公開します。',
              en: 'Open to public as desinged search',
            },
          option: 'NORMAL',
        },
        {
          label: { ja: 'ギャラリー用検索', en: 'For Gallery' },
          disabled: props.editstate.cs.type === 'NOTE',
            description: {
              ja: '一般公開しても、テーマ別検索一覧には表示されず、ギャラリーに埋め込んで使います。',
              en: 'Embbeded in gallery, and not appeared on desinged search. ',
            },
          option: 'NOTE',
        },
      ],
    })
    if (!type) return
    props.editstate.cs.type = type
    snackbarMsg({ ja: '反映するには保存してください' })
  } catch {}
}

const publicationModal = ref<InstanceType<typeof PublicationModal>>()
const managePublish = async () => {
  try {
    const publicationLevel = await publicationModal.value.openAwait({
      data: props.editstate.cs
    })
    if (!publicationLevel) return
    props.editstate.cs.permission.publicationLevel = publicationLevel
    snackbarMsg({ ja: '反映するには保存してください' })
  } catch (error) {}
}

const permissionModal = ref<InstanceType<typeof PermissionModal>>()
const managePermission = async () => {
  try {
    const permission = await permissionModal.value.openAwait({
      data: { permission: props.editstate.cs.permission }
    })
    if (!permission) return
    props.editstate.cs.permission = permission
    snackbarMsg({ ja: '反映するには保存してください' })
  } catch (error) {}
}

//キーワード検索
const csKeywordModal = ref<InstanceType<typeof CsKeywordModal>>()
const setKeyword = async () => {
  try {
    const cs = await csKeywordModal.value.openAwait({
      editstate: props.editstate
    })
    if (!cs) return
    props.editstate.cs.keywordFields = cs.keywordFields
    props.editstate.cs.keywordPlaceholder = cs.keywordPlaceholder
  } catch (cancel) {}
}

// 検索例
const csSampleModal = ref<InstanceType<typeof CsSampleModal>>()
const editSample = async (target?: SearchQuery) => {
  try {
    const query = await csSampleModal.value.openAwait({
      editstate: props.editstate,
      query: target
    })
    if (target) {
      updateToArray(props.editstate.cs.sampleQuery, target, query)
    } else {
      props.editstate.cs.sampleQuery.push(query)
    }
  } catch (cancel) {}
}

const sortSampleModal = ref<InstanceType<typeof UiSortModal>>()
const sortSample = async () => {
  try {
    const sorted = await sortSampleModal.value.openAwait(props.editstate.cs.sampleQuery)
    if (!sorted) return
    props.editstate.cs.sampleQuery = sorted
  } catch (cancel) {}
}

const searchSample = (q) => {
  ss.value.restoreQuery(q)
}

//データベースの選択
const dbSelectorModal = ref<InstanceType<typeof DbSelectorModal>>()
const selectDatabase = async () => {
  try {
    const databaseIds = await dbSelectorModal.value.openAwait({
      multiple: true,
      selected: props.editstate.cs.databaseIds
    })
    if (!databaseIds) return
    props.editstate.cs.databaseIds = databaseIds
  } catch (e) {}
}

//検索詳細項目
const csFieldModal = ref<InstanceType<typeof CsFieldModal>>()
const editSearchField = async (target?: CustomSearchField) => {
  try {
    const field = await csFieldModal.value.openAwait({
      editstate: props.editstate,
      field: target
    })
    if (target) {
      updateToArray(props.editstate.cs.searchFields, target, field)
    } else {
      props.editstate.cs.searchFields.push(field)
    }
  } catch (cancel) {}
}

const sortSearchFieldModal = ref<InstanceType<typeof UiSortModal>>()
const sortSearchField = async () => {
  try {
    const sorted = await sortSearchFieldModal.value.openAwait(props.editstate.cs.searchFields)
    if (!sorted) return
    props.editstate.cs.searchFields = sorted
  } catch (cancel) {}
}

//検索制約
const csConstraintModal = ref<InstanceType<typeof CsConstraintModal>>()
const editSearchConstraint = async (target?: CustomSearchConstraint) => {
  try {
    const constraint = await csConstraintModal.value.openAwait({
      editstate: props.editstate,
      constraint: target
    })
    if (target) {
      updateToArray(props.editstate.cs.constraints, target, constraint)
    } else {
      props.editstate.cs.constraints.push(constraint)
    }
  } catch (cancel) {}
}

defineExpose({ undo, redo })
</script>

<style lang="scss" scoped>
@use '@/assets/styles/scss/vue' as *;

.cs-editor {
  width: 100%;
  height: 100%;
  overflow-y: scroll;
  overflow-x: hidden;
  $toolbar-height: 2rem;
  h2 {
    margin-top: 0;
  }
  .cs-editor-head {
    position: sticky;
    top: 0;
    right: 0;
    z-index: 1000;
    display: flex;
    justify-content: flex-end;
    .cs-editor-menu {
      height: $toolbar-height;
      z-index: 2;
      display: flex;
      justify-content: space-evenly;
      align-items: center;
      background-color: $jps-gray-50;
      border-bottom-left-radius: 6px;
      box-shadow:
        0.3px 0.3px 17px #d7d7d7,
        -0.3px -0.3px 17px #ffffff;
      > * {
        margin-left: 0.5rem;
        margin-right: 0.5rem;
      }
      .jps-text-button {
        display: flex;
        align-items: center;
        gap: 4px;
      }
    }
  }

  .cs-editor-main {
    background: $white;
    width: 100%;
    min-height: calc(100% - #{$toolbar-height});
    border-top-left-radius: 6px;
    h2 {
      display: flex;
      align-items: center;
    }
    .cs-part-editor {
      border: 1px solid $jps-gray-300;
      border-radius: $global-radius-large;
      background: $jps-gray-100;
      @include panel-shadow();
      margin: 1rem 0;
      padding: 1rem;
    }
    .editor-cs-header {
      .editor-title {
        display: flex;
        align-items: center;
        margin-bottom: 0.75rem;
        @include is-mobile() {
          flex-direction: column;
          gap: 0.5rem;
        }
        label {
          margin-right: 0.5rem;
          min-width: 4rem;
          font-size: 0.75rem;
        }
        .title-field {
          font-size: 1.5rem;
          flex-grow: 1;
          max-width: 40rem;
          text-align: center;
        }
      }
      .editor-description {
        display: flex;
        align-items: center;
        @include is-mobile() {
          flex-direction: column;
          gap: 0.5rem;
        }
        label {
          margin-right: 0.5rem;
          min-width: 4rem;
          font-size: 0.75rem;
        }
        .title-field {
          flex-grow: 1;
          max-width: 40rem;
          text-align: center;
        }
      }

      .sample-query {
        padding-top: 12px;
        dl {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          dt {
            display: flex;
            align-items: center;
          }
          dd {
            display: flex;
            align-items: center;
            gap: 0.25rem;
            a {
              text-decoration: underline;
            }
          }
          .sample-keyword {
            border: 1px dashed $border-grey;
            padding: 2px;
          }
        }
      }
      .cs-editor-help {
        margin-top: 1rem;
        border: 1px solid $jps-gray-300;
        &:focus-within,
        &:hover {
          border: 1px solid $jps-key-blue;
        }
        padding: 1px;
        margin-bottom: 1rem;
      }
    }
    .cs-editor-section {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      label {
        font-size: 0.75rem;
      }
    }

    .cs-editor-search-box {
      padding: 1rem;
    }

    .jps-icon svg {
      text-align: text-top;
    }

    .search-field-edit {
      padding: 0.25rem 0.75rem;
      display: flex;
      align-items: center;
      gap: 0.25rem;
    }
    .item-search-result {
      padding: 0 2rem;
    }
  }

  .cs-editor-heading {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
}
</style>
